// ============================================================
// PusherService — リアルタイム通知接続
//
// Pusher チャンネルを購読し、受信したイベントで
// reservationSyncService / vehicleSyncService のキャッシュを即時更新。
// ============================================================

import Pusher from 'pusher-js';
import { isTrainingMode } from '../training/trainingMode';
import { mapReservation, mapVehicle } from '../api/dispatchMappers';
import { reservationSyncService } from './reservationSyncService';
import { vehicleSyncService } from './vehicleSyncService';
import { saveChangeToHistory } from './changeHistory';
import { getPusherConfig, isLineEnabled, getInstance, getSiteOrigin } from '../config/backend';
import { getAuthToken } from '../api/dispatchClient';
import { logEvent } from '../lib/eventLog';
import { buildChannelAuthorization, dispatchChannelNames, lineChannelNames, isPrivateChannel } from '../lib/pusherAuth';
import { getDeviceId, getDeviceName } from './deviceIdentifier';
import { FIELD_LABELS } from '../constants/fieldLabels';
import { appendDigestLog } from '../api/digestApi';
import type { ChangeRecord, FieldChange } from '../types';

// ---- 全 Pusher イベントの raw 収集（2026-07-26） ----
// ダイジェストウィンドウに表示するのは message/transfer/vehicle の3種だけだが、
// DB(digest_logs) には受信した全イベントを type='raw' で蓄積する（後からの分析・検索用）。
// event_key はサーバ由来の id/updated_at/created_at から決定的に生成し、
// 複数ブラウザの同時書き込みは DB 側の UNIQUE + INSERT IGNORE で重複排除される。

/** 決定的な文字列ハッシュ（djb2）。タイムスタンプを持たないイベントのキー用 */
const rawHash = (s: string): string => {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

const formatRawTime = (value?: string): string => {
  const d = value ? new Date(value) : new Date();
  const t = isNaN(d.getTime()) ? new Date() : d;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
};

const logRawPusherEvent = (eventName: string, data: any): void => {
  try {
    const d = data || {};
    const serverTime: string | undefined = d.updated_at || d.created_at || undefined;
    // 安定キー: id + サーバ時刻があればそれを使う。なければペイロードのハッシュ
    const key = d.id != null && serverTime
      ? `raw:${eventName}:${d.id}:${serverTime}`
      : d.id != null
        ? `raw:${eventName}:${d.id}`
        : `raw:${eventName}:${rawHash(JSON.stringify(d))}`;
    // 概要テキスト: 検索で引っかかる代表フィールドを付ける
    const label = d.customer_name || d.vehicle_code || d.vehicle_label
      || (typeof d.content === 'string' ? d.content.slice(0, 60) : '')
      || (d.id != null ? `#${d.id}` : '');
    appendDigestLog({
      event_key: key.slice(0, 191),
      log_time: formatRawTime(serverTime),
      type: 'raw',
      text: `[${eventName}] ${label}`.trim(),
      meta: { event: eventName, id: d.id ?? null },
    });
  } catch { /* 収集失敗は本処理に影響させない */ }
};

/**
 * 複数チャンネルへ同じハンドラを束ねる（移行期間の public / private 両購読用）。
 * bind / bind_global を各チャンネルに転送する。backend が private に切り替わるまでは
 * public 側にだけイベントが来るため、同じイベントが二重に届くことはない。
 */
const subscribeMulti = (pusher: any, names: string[], label: string) => {
  const channels = names.map((name) => {
    const ch = pusher.subscribe(name);
    ch.bind('pusher:subscription_error', (err: any) => {
      // private 側は backend/認証エンドポイントが未対応の間は失敗し続ける想定。public 側の失敗は異常。
      const status = err?.status ?? err?.data?.status ?? null;
      console.warn(`[PusherService] subscription_error: ${name}`, err);
      logEvent({
        action: 'pusher.subscribe',
        target: name,
        result: 'fail',
        detail: { label, kind: isPrivateChannel(name) ? 'private' : 'public', status },
        err: err?.error ?? err?.message ?? err,
      });
    });
    return ch;
  });
  return {
    names,
    channels,
    bind: (event: string, cb: (data: any) => void) => channels.forEach((c) => c.bind(event, cb)),
    bind_global: (cb: (eventName: string, data: any) => void) => channels.forEach((c) => c.bind_global(cb)),
  };
};

type PusherEventCallback = (eventName: string, data: any) => void;

interface PusherState {
  connected: boolean;
  socketId: string | null;
  state: string;
}

class PusherService {
  pusher: any;
  channel: any;
  lineChannel: any;
  listeners: PusherEventCallback[];
  lineListeners: PusherEventCallback[];
  connected: boolean;
  /** 一度でも接続できたか（初回接続と再接続を区別するため） */
  hasConnectedOnce: boolean;
  /** 再接続時に呼ぶコールバック群（取りこぼしイベントのキャッチアップ同期等に使う） */
  reconnectListeners: Set<() => void>;

  constructor() {
    this.pusher = null;
    this.channel = null;
    this.lineChannel = null;
    this.listeners = [];
    this.lineListeners = [];
    this.connected = false;
    this.hasConnectedOnce = false;
    this.reconnectListeners = new Set();
  }

  /**
   * 再接続イベントの購読。
   * Pusher の 'connected' は初回接続でも発火するため、2回目以降（= 再接続）のみ通知する。
   * 切断中に取りこぼした更新を forceSync 等でキャッチアップする用途。
   */
  onReconnect(callback: () => void): () => void {
    this.reconnectListeners.add(callback);
    return () => this.reconnectListeners.delete(callback);
  }

  connect(): void {
    if (this.pusher) return;

    // 店舗ごとに別の Pusher アプリを使う(同一アカウント内の別アプリ)。
    // booking と pit-booking は同一バンドル共有のため、env ではなく instances.ts の
    // 店舗別 key/cluster を解決して使う(那覇は null → env にフォールバック)。
    const { key, cluster } = getPusherConfig();

    if (!key || !cluster) {
      console.warn('[PusherService] Pusher app key/cluster が未設定のため接続しません(ポーリングで動作)');
      return;
    }

    // デバッグ用: Pusher のログを有効化（本番では無効にする）
    (Pusher as any).logToConsole = process.env.NODE_ENV === 'development';

    // private チャンネル認証: ログイン済みトークンを Bearer で自社の pusher_auth.php へ送る。
    // トークンが無い（未ログイン）場合は認証設定を付けない = private は購読できず public のみ。
    const channelAuthorization = buildChannelAuthorization(getSiteOrigin(), getAuthToken());
    this.pusher = new Pusher(key, {
      cluster,
      forceTLS: true,
      ...(channelAuthorization ? { channelAuthorization } : {}),
    });

    // 接続状態の監視
    this.pusher.connection.bind('connected', () => {
      const isReconnect = this.hasConnectedOnce;
      this.connected = true;
      this.hasConnectedOnce = true;
      console.log('[PusherService] Connected to Pusher', isReconnect ? '(reconnect)' : '(initial)');
      // 再接続時のみ: 切断中に取りこぼした更新をキャッチアップ
      if (isReconnect) {
        this.reconnectListeners.forEach(cb => {
          try { cb(); } catch (e) { console.error('[PusherService] reconnect listener error:', e); }
        });
      }
    });

    this.pusher.connection.bind('disconnected', () => {
      this.connected = false;
      console.log('[PusherService] Disconnected from Pusher');
    });

    this.pusher.connection.bind('error', (err: any) => {
      console.error('[PusherService] Connection error:', err);
    });

    // チャンネル購読
    this.channel = subscribeMulti(this.pusher, dispatchChannelNames(), 'dispatch');

    this.channel.bind('pusher:subscription_succeeded', () => {
      console.log('[PusherService] Subscribed to channel(s):', this.channel.names.join(', '));
    });


    // 全イベントをキャッチ → リスナーに通知 + raw 収集（digest_logs）
    this.channel.bind_global((eventName: string, data: any) => {
      if (eventName.startsWith('pusher:')) return;
      // トレーニングモード中は本番の更新を画面に混ぜない（2026-08-24）
      if (isTrainingMode()) return;
      console.log(`[PusherService] Event: ${eventName}`, data);
      logRawPusherEvent(eventName, data);
      this.notifyListeners(eventName, data);
    });

    // ===== LINE Chat チャンネル =====
    // LINE 連携が無効な店舗(宮古)では購読しない。アプリを分けているので実害は無いが、
    // 将来アプリを共用した場合に他店舗の LINE イベントを受けてしまうのを防ぐ。
    if (isLineEnabled()) {
      this.lineChannel = subscribeMulti(this.pusher, lineChannelNames(), 'line');

      this.lineChannel.bind('pusher:subscription_succeeded', () => {
        console.log('[PusherService] Subscribed to channel(s):', this.lineChannel.names.join(', '));
      });

      this.lineChannel.bind_global((eventName: string, data: any) => {
        if (eventName.startsWith('pusher:')) return;
        if (isTrainingMode()) return;
        console.log(`[PusherService] LINE Event: ${eventName}`, data);
        logRawPusherEvent(eventName, data);
        this.notifyLineListeners(eventName, data);
      });
    }

    // ===== 予約イベント =====
    this.channel.bind('reservation.created', (data: any) => {
      console.log('[PusherService] reservation.created:', data);
      this.handleReservationUpdate(data);
      this.sendBrowserNotification(
        '新しい予約',
        data?.customer_name
          ? `${data.customer_name} の予約が作成されました`
          : '新しい予約が作成されました'
      );
    });

    this.channel.bind('reservation.updated', (data: any) => {
      console.log('[PusherService] reservation.updated:', data);
      this.handleReservationUpdate(data);
      this.sendBrowserNotification(
        '予約更新',
        data?.customer_name
          ? `${data.customer_name} の予約が更新されました`
          : '予約が更新されました'
      );
    });

    // ===== 車両イベント =====
    this.channel.bind('vehicle.updated', (data: any) => {
      console.log('[PusherService] vehicle.updated:', data);
      this.handleVehicleUpdate(data);
    });

    this.channel.bind('vehicle.swapped', (data: any) => {
      console.log('[PusherService] vehicle.swapped:', data);
      if (data?.vehicles && Array.isArray(data.vehicles)) {
        for (const v of data.vehicles) {
          this.handleVehicleUpdate(v);
        }
      }
    });

    // ===== マスターデータイベント =====
    this.channel.bind('vehicle-type.updated', (data: any) => {
      console.log('[PusherService] vehicle-type.updated:', data);
      // 車種マスタ更新 → 車両データを再取得して反映
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    this.channel.bind('status.updated', (data: any) => {
      console.log('[PusherService] status.updated:', data);
      // ステータスマスタ更新 → 全データ再取得
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    this.channel.bind('vehicle-check.updated', (data: any) => {
      console.log('[PusherService] vehicle-check.updated:', data);
      // Pusher でキャッシュ即時更新済みのため forceSync 不要
    });

    // ===== スタッフ優先度イベント =====
    this.channel.bind('staff-priority.updated', (data: any) => {
      console.log('[PusherService] staff-priority.updated:', data);
      // 自分自身が発火したイベントは無視（localStorage は既に更新済み）
      try {
        const myDeviceId = getDeviceId();
        if (data?.device_id && data.device_id === myDeviceId) return;
      } catch { /* ignore */ }
      // 他店舗のイベントは適用しない（2026-08-02 追加の保険）。
      // priorities はスタッフIDのマップで、下の setItem が**全置換**するため、
      // 他店舗のマップを適用すると出勤表が別人で丸ごと入れ替わる。
      // 送信側(broadcast_priority.php)を店舗別にしたので本来ここには届かないが、
      // 二重の防御として残す。⚠️ instance 未指定は旧バンドルからの送信なので適用する
      // （厳格にすると移行中に那覇の同期が止まる）。
      if (data?.instance && data.instance !== getInstance()) {
        console.log('[PusherService] 他店舗の staff-priority.updated を無視:', data.instance);
        return;
      }
      if (data?.priorities && typeof data.priorities === 'object') {
        try {
          localStorage.setItem('staff_priorities', JSON.stringify(data.priorities));
          // 既存リスナー（onStaffPriorityChange 経由）を起動
          window.dispatchEvent(new CustomEvent('staff-priority-updated', { detail: data.priorities }));
        } catch (e) {
          console.error('[PusherService] Failed to apply staff-priority.updated:', e);
        }
      }
    });

    // ===== 支払いイベント =====
    this.channel.bind('payment.updated', (data: any) => {
      console.log('[PusherService] payment.updated:', data);
      // ブラウザ通知
      this.sendBrowserNotification(
        '支払い更新',
        data?.contact_code
          ? `予約 ${data.contact_code} の支払いが更新されました`
          : '支払いが更新されました'
      );
      // 変更履歴に記録（PusherToast用）
      const record: ChangeRecord = {
        id: `${Date.now()}_payment_${data?.contact_code || 'unknown'}`,
        timestamp: new Date().toISOString(),
        entityType: 'payment',
        entityId: data?.contact_code || '',
        changeType: 'update',
        changes: [],
        source: 'pusher',
        summary: `${data?.contact_code || ''} の支払いが更新されました（${data?.invoice_status || ''}）`,
      };
      saveChangeToHistory(record);
      window.dispatchEvent(new CustomEvent('pusher-change-recorded', { detail: record }));
    });

    console.log('[PusherService] Connecting...', { key: key.slice(0, 6) + '...', cluster });
  }

  // ===== キャッシュ即時更新 + 変更履歴記録 =====

  /**
   * 2つのオブジェクト間の差分フィールドを取得
   */
  computeDiff(oldObj: any, newObj: any, skipFields: Set<string> = new Set()): FieldChange[] {
    const changes: FieldChange[] = [];
    if (!oldObj || !newObj) return changes;
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

    for (const key of allKeys) {
      if (key.startsWith('_') || skipFields.has(key)) continue;

      const oldVal = oldObj[key];
      const newVal = newObj[key];

      // 空値同士はスキップ
      const isOldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
      const isNewEmpty = newVal === null || newVal === undefined || newVal === '';
      if (isOldEmpty && isNewEmpty) continue;

      // オブジェクト・配列はJSON比較
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          label: (FIELD_LABELS as any)[key] || key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }
    return changes;
  }

  /** 予約変更用のスキップフィールド */
  static RESERVATION_SKIP_FIELDS = new Set([
    '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'resourceId', 'resource_id', 'vehicleId',
    'status_name', 'status_code',
    'communication_tools_display',
    'booking_date', 'bookingDate',
    'past_reservation_count',
    'resource_id_r',
    'vehicle', 'detail', // ネストオブジェクト（detail内は別途比較）
    'bg_color', 'text_color',
    'start', 'end', // start_date/end_date と重複
    'reservation_id', // id と重複
    'json_data',
  ]);

  /** 車両変更用のスキップフィールド */
  static VEHICLE_SKIP_FIELDS = new Set([
    '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'status_name', 'status_code',
    'last_checked_at',
    'check_situations',
    'vehicle_type', // ネストオブジェクト
    'resourceId', 'resource_id', 'vehicleId', 'vehicle_id',
    'bg_color', 'text_color', 'color1', 'color',
    'car_type', 'car_type_id', 'car_type_code',
    'vehicle_type_id', 'vehicle_type_page_id', 'vehicle_type_page_ids',
  ]);

  /**
   * 変更履歴レコードを保存し、UIにリフレッシュイベントを発火
   */
  saveAndNotifyHistory(record: ChangeRecord): void {
    saveChangeToHistory(record);
    // ChangeManagerContext にリフレッシュを通知するカスタムイベント
    window.dispatchEvent(new CustomEvent('pusher-change-recorded', { detail: record }));
    console.log('[PusherService] Change history recorded:', (record as any).summary);
  }

  /** 予約データをキャッシュに即時反映 + 変更履歴記録 */
  handleReservationUpdate(data: any): void {
    if (!data || !data.id) return;
    try {
      const mapped = mapReservation(data);
      if (!mapped) return;

      const id = String(mapped.id);
      const existing = reservationSyncService.cachedReservations?.get(id);

      // updated_at 比較: 既存データより古ければ無視
      // 同時刻(=)は処理する: applyLocalUpdate（保存の即時反映）でキャッシュが先に
      // 同じ updated_at になるため、<= だと自己変更のエコーがスキップされ変更履歴に残らない。
      // キャッシュ再セットは冪等なので同時刻の再処理は無害。
      if (existing?.updated_at && mapped.updated_at) {
        if (new Date(mapped.updated_at as string) < new Date(existing.updated_at as string)) {
          console.log(`[PusherService] Skipping older reservation update: ${id}`);
          return;
        }
      }

      // 変更履歴を記録（キャッシュ更新前に差分を取る）
      const changeType = existing ? 'update' : 'create';
      const changes = existing ? this.computeDiff(existing, mapped, PusherService.RESERVATION_SKIP_FIELDS) : [];
      const customerName = mapped.customer_name || '不明';
      const fieldCount = changes.length;

      let summary: string;
      if (changeType === 'create') {
        summary = `予約 #${mapped.id}（${customerName}）が追加されました`;
      } else if (fieldCount > 0) {
        const fieldNames = changes.slice(0, 2).map(f => f.label).join('、');
        const more = fieldCount > 2 ? ` 他${fieldCount - 2}件` : '';
        summary = `予約 #${mapped.id}（${customerName}）の${fieldNames}${more}が更新されました`;
      } else {
        summary = `予約 #${mapped.id}（${customerName}）が更新されました`;
      }

      const record: ChangeRecord = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType: 'reservation',
        entityId: mapped.id,
        changeType,
        changeTypeLabel: changeType === 'create' ? '新規作成' : '更新',
        source: 'pusher',
        deviceId: getDeviceId(),
        deviceName: getDeviceName(),
        changes,
        summary,
        metadata: { pusherEvent: changeType === 'create' ? 'reservation.created' : 'reservation.updated' },
      };

      this.saveAndNotifyHistory(record);

      // キャッシュ更新（2026-07-27: 置換→マージに変更）
      // broadcast ペイロードに detail の一部フィールドが欠けている場合、置換だと
      // 保存直後の値（例: 支払い確認 payment=1）がエコーで消され「トグルが戻る」誤動作になる。
      // 既存キャッシュをベースに detail は深マージして、欠落フィールドで上書きしない。
      const mergedRes = existing
        ? { ...existing, ...mapped, detail: { ...((existing as any).detail || {}), ...((mapped as any).detail || {}) } }
        : mapped;
      reservationSyncService.cachedReservations?.set(id, mergedRes);
      const all = reservationSyncService.getReservations?.() || [];
      reservationSyncService.listeners?.forEach((cb: any) => {
        try { cb([{ type: 'pusher_update', id }], all); } catch (e) { /* ignore */ }
      });
      console.log(`[PusherService] Reservation cache updated: ${id} (${mapped.customer_name})`);
    } catch (e) {
      console.error('[PusherService] handleReservationUpdate error:', e);
    }
  }

  /** 車両データをキャッシュに即時反映 + 変更履歴記録 */
  handleVehicleUpdate(data: any): void {
    if (!data || !data.id) return;
    try {
      const mapped = mapVehicle(data);
      if (!mapped) return;

      const id = String(mapped.id);
      // キャッシュキーは fetch 時の v.id（通常 number）のため String(id) の直接 get はミスする。
      // getVehicle() が number/string 揺れを吸収する（従来はここが常にミスし、
      // 毎回 create 扱い + string キーの重複エントリが発生していた）。
      const existing = vehicleSyncService.getVehicle(mapped.id);

      // updated_at 比較（同時刻(=)は処理する: handleReservationUpdate と同じ理由。
      // applyLocalUpdate 併用時も自己変更のエコーを変更履歴に残すため）
      if (existing?.updated_at && mapped.updated_at) {
        if (new Date(mapped.updated_at as string) < new Date(existing.updated_at as string)) {
          console.log(`[PusherService] Skipping older vehicle update: ${id}`);
          return;
        }
      }

      // 変更履歴を記録（キャッシュ更新前に差分を取る）
      const changeType = existing ? 'update' : 'create';
      const changes = existing ? this.computeDiff(existing, mapped, PusherService.VEHICLE_SKIP_FIELDS) : [];
      const vehicleName = mapped.vehicle_code || '不明';
      const fieldCount = changes.length;

      let summary: string;
      if (changeType === 'create') {
        summary = `車両「${vehicleName}」が追加されました`;
      } else if (fieldCount > 0) {
        const fieldNames = changes.slice(0, 2).map(f => f.label).join('、');
        const more = fieldCount > 2 ? ` 他${fieldCount - 2}件` : '';
        summary = `車両「${vehicleName}」の${fieldNames}${more}が更新されました`;
      } else {
        summary = `車両「${vehicleName}」が更新されました`;
      }

      const record: ChangeRecord = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType: 'vehicle',
        entityId: mapped.id,
        changeType,
        changeTypeLabel: changeType === 'create' ? '新規作成' : '更新',
        source: 'pusher',
        deviceId: getDeviceId(),
        deviceName: getDeviceName(),
        changes,
        summary,
        metadata: {
          pusherEvent: 'vehicle.updated',
          // ダイジェストウィンドウ用（2026-07-26）: 全ブラウザ共通の安定した重複排除キーを
          // 作れるよう、サーバ由来の updated_at と表示用フィールドを添付する
          entityUpdatedAt: (mapped as any).updated_at ?? null,
          vehicleCode: (mapped as any).vehicle_code ?? null,
          statusOld: existing != null ? Number((existing as any).status) : null,
          statusNew: (mapped as any).status != null ? Number((mapped as any).status) : null,
          locationStatusNew: (mapped as any).location_status != null ? Number((mapped as any).location_status) : null,
        },
      };

      this.saveAndNotifyHistory(record);

      // キャッシュ更新（applyLocalUpdate がキー正規化・マージ・リスナー通知まで行う）
      vehicleSyncService.applyLocalUpdate(mapped);
      console.log(`[PusherService] Vehicle cache updated: ${id} (${mapped.vehicle_code})`);
    } catch (e) {
      console.error('[PusherService] handleVehicleUpdate error:', e);
    }
  }

  // ===== ブラウザ通知 =====

  /** タブが非アクティブならブラウザ通知を送信 */
  sendBrowserNotification(title: string, body: string): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    // タブがアクティブなら通知しない（アプリ内トーストで十分）
    if (document.visibilityState === 'visible') return;
    try {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'dispatch-update',
      });
    } catch (e) {
      console.error('[PusherService] Notification error:', e);
    }
  }

  disconnect(): void {
    if (this.pusher) {
      [...dispatchChannelNames(), ...lineChannelNames()].forEach((n) => this.pusher.unsubscribe(n));
      this.pusher.disconnect();
      this.pusher = null;
      this.channel = null;
      this.lineChannel = null;
      this.connected = false;
      console.log('[PusherService] Disconnected');
    }
  }

  /** イベントリスナーを追加 (dispatch-updates) */
  subscribe(callback: PusherEventCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /** LINE Chat イベントリスナーを追加 */
  subscribeLine(callback: PusherEventCallback): () => void {
    this.lineListeners.push(callback);
    return () => {
      this.lineListeners = this.lineListeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners(eventName: string, data: any): void {
    for (const cb of this.listeners) {
      try { cb(eventName, data); } catch (e) { console.error('[PusherService] Listener error:', e); }
    }
  }

  notifyLineListeners(eventName: string, data: any): void {
    for (const cb of this.lineListeners) {
      try { cb(eventName, data); } catch (e) { console.error('[PusherService] LINE listener error:', e); }
    }
  }

  getState(): PusherState {
    return {
      connected: this.connected,
      socketId: this.pusher?.connection?.socket_id ?? null,
      state: this.pusher?.connection?.state ?? 'not_initialized',
    };
  }
}

// シングルトン
export const pusherService = new PusherService();

if (typeof window !== 'undefined') {
  (window as any).__pusherService = pusherService;
}
